// Implements: F-IDN-026
/**
 * The Invitaciones table (v3 `vInvites`): address, role, team, status, expiry and actions. A
 * pending or expired invitation can be copied (a NEW link, the previous one stops working); a
 * pending one can also be revoked; a used or revoked one offers nothing. There is no "Reenviar":
 * nothing is sent by e-mail yet (P2).
 */

import type { ReactElement } from "react";

import { useTranslation } from "../../../shared/i18n";
import { Badge } from "../../../shared/ui/atoms/Badge";
import type { BadgeTone } from "../../../shared/ui/atoms/Badge";
import { Button } from "../../../shared/ui/atoms/Button";
import { Icon } from "../../../shared/ui/atoms/Icon";
import { dayText } from "../../../shared/ui/dayText";
import type { InvitationStatus, InvitationView } from "../../../types/organization";
import { invitationRoleLabelKey } from "../model/roles";
import styles from "./Organization.module.css";

type StatusKey =
  | "organization.invitations.status.pending"
  | "organization.invitations.status.expired"
  | "organization.invitations.status.consumed";

const STATUS: Readonly<Record<InvitationStatus, { readonly key: StatusKey; readonly tone: BadgeTone }>> = {
  pending: { key: "organization.invitations.status.pending", tone: "info" },
  expired: { key: "organization.invitations.status.expired", tone: "warning" },
  consumed: { key: "organization.invitations.status.consumed", tone: "neutral" },
};

export interface InvitationsTableProps {
  readonly invitations: readonly InvitationView[];
  readonly teamNames: ReadonlyMap<string, string>;
  /** Names of the people seated in the organization's teams, to say whom a lead invitation replaces. */
  readonly personNames: ReadonlyMap<string, string>;
  /** The invitation whose copy is in flight (its button is busy). */
  readonly copying: string | null;
  readonly onCopy: (invitation: InvitationView) => void;
  readonly onRevoke: (invitation: InvitationView) => void;
}

export function InvitationsTable({
  invitations,
  teamNames,
  personNames,
  copying,
  onCopy,
  onRevoke,
}: InvitationsTableProps): ReactElement {
  const { t } = useTranslation();

  /** The Rol cell; a lead invitation that converts on acceptance names whom it replaces (owner 2026-10-19). */
  function roleOf(invitation: InvitationView): string {
    const replaced = invitation.replaces_lead_user_id ?? null;
    if (replaced === null) return t(invitationRoleLabelKey(invitation));
    const name = personNames.get(replaced);
    return name === undefined
      ? t("organization.invitations.replacesLeadUnknown")
      : t("organization.invitations.replacesLead", { name });
  }
  return (
    <div className={styles.scroll}>
      <table className={styles.table}>
        <thead>
          <tr>
            <th scope="col">{t("organization.invitations.column.email")}</th>
            <th scope="col">{t("organization.invitations.column.role")}</th>
            <th scope="col">{t("organization.invitations.column.team")}</th>
            <th scope="col">{t("organization.invitations.column.status")}</th>
            <th scope="col">{t("organization.invitations.column.expires")}</th>
            <th scope="col">
              <span className={styles.srOnly}>{t("organization.actions")}</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {invitations.map((invitation) => {
            const status = STATUS[invitation.status];
            const team = invitation.team_id === null ? undefined : teamNames.get(invitation.team_id);
            return (
              <tr key={invitation.id}>
                <td className={styles.strong}>{invitation.email}</td>
                <td>{roleOf(invitation)}</td>
                <td>{team ?? <span className={styles.meta}>{t("common.value.unknown")}</span>}</td>
                <td>
                  <Badge tone={status.tone}>{t(status.key)}</Badge>
                </td>
                <td className={styles.meta}>
                  {invitation.status === "pending"
                    ? t("organization.invitations.expiresOn", { date: dayText(invitation.expires_at) })
                    : invitation.status === "expired"
                      ? t("organization.invitations.expiredOn", { date: dayText(invitation.expires_at) })
                      : t("common.value.unknown")}
                </td>
                <td>
                  {invitation.status === "consumed" ? null : (
                    <div className={styles.actions}>
                      <Button
                        variant="ghost"
                        iconOnly
                        label={t("organization.invitations.copyFor", { email: invitation.email })}
                        loading={copying === invitation.id}
                        onClick={() => {
                          onCopy(invitation);
                        }}
                      >
                        <Icon name="copy" />
                      </Button>
                      {invitation.status === "pending" ? (
                        <Button
                          variant="ghost"
                          onClick={() => {
                            onRevoke(invitation);
                          }}
                        >
                          {t("organization.invitations.revoke")}
                        </Button>
                      ) : null}
                    </div>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
